import { Text, IconButton, Icon, useDisclosure, Tooltip } from '@chakra-ui/react';
// import debounce from 'lodash/debounce';
import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CellValue } from 'react-table';

import { IconBan, IconThunder } from 'assets/icons';
import { ConfirmationModal } from 'components/Modal';
import Table from 'components/Table';
import { useAcceptPendingShift } from 'hooks/scheduling/useAcceptPendingShift';
import {
  IPendingShiftSearchQuery,
  useFetchPaginatedPendingShift,
} from 'hooks/scheduling/useFetchPaginattedPendingShifts';
import { useRejectPendingShift } from 'hooks/scheduling/useRejectPendingShift';
import { useCompanyInfo } from 'store/useUserStore';
import { formatYearMonthDayTime } from 'utils/format';
import toaster from 'utils/toaster';

const initialPendingShiftSearchQuery: IPendingShiftSearchQuery = {
  page: 1,
  page_size: 25,
  search: '',
};

const PendingShift: React.FC<unknown> = () => {
  const { t } = useTranslation(['common', 'scheduling']);

  const companyInfo = useCompanyInfo();

  const [enableAcceptPendingSfhit, setEnableAcceptPendingShift] = useState<boolean>(false);
  const [enableRejectPendingShift, setEnableRejectPendingShift] = useState<boolean>(false);
  const [dialogId, setDialogId] = useState<null | string>(null);
  const [searchQuery, setSearchQuery] = useState<IPendingShiftSearchQuery>(initialPendingShiftSearchQuery);

  const { isOpen: isRejectModalOpen, onOpen: onRejectModalOpen, onClose: onRejectModalClose } = useDisclosure();
  const { isOpen: isAcceptModalOpen, onOpen: onAcceptModalOpen, onClose: onAcceptModalClose } = useDisclosure();

  const { status, data, refetch } = useFetchPaginatedPendingShift(searchQuery);
  const { isFetching: acceptPendingshiftIsFetching } = useAcceptPendingShift(dialogId, {
    enabled: enableAcceptPendingSfhit,
    onSuccess: (data: any) => {
      if (data.message) {
        toaster.success({ description: data.message });
        setEnableAcceptPendingShift(false);
        setDialogId(null);
        onAcceptModalClose();
        refetch();
      }
    },
  });

  const { isFetching: rejectPendingShiftIsFetching } = useRejectPendingShift(dialogId, {
    enabled: enableRejectPendingShift,
    onSuccess: (data: any) => {
      if (data.message) {
        toaster.success({ description: data.message });
        setEnableRejectPendingShift(false);
        setDialogId(null);
        onRejectModalClose();
        refetch();
      }
    },
  });

  const columns = useMemo(
    () => [
      {
        Header: t('common:title'),
        accessor: 'title',
        width: 200,
        Cell: (data: CellValue) => {
          return (
            <Text fontWeight="semibold" color="primary.500" mb="1">
              {data.value}
            </Text>
          );
        },
      },
      {
        Header: t('common:startDate'),
        accessor: 'start_date',
        width: 200,
        Cell: (data: CellValue) => <Text>{data.value ? formatYearMonthDayTime(data.value) : '- '}</Text>,
      },
      {
        Header: t('common:endDate'),
        accessor: 'end_date',
        width: 200,
        Cell: (data: CellValue) => <Text>{data.value ? formatYearMonthDayTime(data.value) : '- '}</Text>,
      },
      {
        Header: t('common:notes'),
        accessor: 'notes',
        Cell: (data: CellValue) => <Text>{data.value || '- '}</Text>,
      },
      {
        Header: t('common:status'),
        accessor: 'status',
        Cell: (data: CellValue) => <Text>{data.value || '- '}</Text>,
      },
      ...(companyInfo?.enableScheduleAccept
        ? [
            {
              Header: '',
              accessor: 'action',
              width: 200,
              Cell: (data: CellValue) => {
                return (
                  <div>
                    {data.row.original.status && data.row.original.status.toLowerCase() === 'pending' ? (
                      <Tooltip label={t('scheduling:approveShift')} hasArrow bg="black">
                        <IconButton
                          aria-label="Approve Shift"
                          icon={<Icon as={IconThunder} />}
                          size="sm"
                          mr="3"
                          background="rgba(38, 103, 201, 0.1)"
                          _hover={{ bg: 'rgba(38, 103, 201, 0.2)' }}
                          _active={{ bg: 'rgba(38, 103, 201, 0.2)' }}
                          onClick={() => handleSingleAccept(data.row.original.id)}
                        />
                      </Tooltip>
                    ) : null}
                    {data.row.original.status && data.row.original.status.toLowerCase() === 'pending' ? (
                      <Tooltip label={t('scheduling:declineShift')} hasArrow bg="black">
                        <IconButton
                          aria-label="Decline Shift"
                          icon={
                            <Icon as={IconBan} sx={{ '& path': { fill: 'secondary.600' } }} color="secondary.600" />
                          }
                          size="sm"
                          bg="rgba(255, 83, 83, 0.1)"
                          _active={{ bg: 'rgba(255, 83, 83, 0.2)' }}
                          _hover={{ bg: 'rgba(255, 83, 83, 0.2)' }}
                          onClick={() => handleSingleReject(data.row.original.id)}
                        />
                      </Tooltip>
                    ) : null}
                  </div>
                );
              },
            },
          ]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // const handleSearchInputChange = useCallback(
  //   debounce((e: React.ChangeEvent<HTMLInputElement>) => {
  //     const searchValue = e.target.value.toLowerCase().trim();
  //     setSearchQuery({ ...searchQuery, page: 1, search: searchValue });
  //   }, 400),
  //   [searchQuery]
  // );

  // deactivate user handler
  const handleSingleReject = (id: string) => {
    setDialogId(id);
    onRejectModalOpen();
  };

  const rejectPendingShift = () => {
    setEnableRejectPendingShift(true);
  };

  // delete user handler
  const handleSingleAccept = (id: string) => {
    setDialogId(id);
    onAcceptModalOpen();
  };
  // delete user handler
  const acceptPendingShift = () => {
    setEnableAcceptPendingShift(true);
  };

  const handlePageChange = useCallback((selectedPageIndex: number) => {
    const updateSearchQuery = (previousSearchQuery: IPendingShiftSearchQuery) => ({
      ...previousSearchQuery,
      page: selectedPageIndex + 1,
    });
    setSearchQuery(updateSearchQuery);
  }, []);

  return (
    <div>
      <ConfirmationModal
        isOpen={isRejectModalOpen}
        onClose={onRejectModalClose}
        title={t('scheduling:rejectShiftModalTitle')}
        subTitle={t('scheduling:rejectShiftModalSubTitle')}
        confirmButtonLabel={t('common:decline')}
        confirmButtonProps={{
          bg: 'rgba(255, 83, 83, 0.1)',
          color: 'secondary.500',
          _hover: {
            bg: 'rgba(255, 83, 83, 0.2)',
          },
          _active: {
            bg: 'rgba(255, 83, 83, 0.2)',
          },
          isLoading: rejectPendingShiftIsFetching,
          onClick: rejectPendingShift,
        }}
      />

      <ConfirmationModal
        isOpen={isAcceptModalOpen}
        onClose={onAcceptModalClose}
        title={t('scheduling:acceptShiftModalTitle')}
        subTitle={t('scheduling:acceptShiftModalSubTitle')}
        confirmButtonLabel={t('common:accept')}
        confirmButtonProps={{
          bg: 'rgba(72,82, 175, 0.1)',
          color: 'primary.500',
          _hover: {
            bg: 'rgba(72,82, 175, 0.2)',
          },
          _active: {
            bg: 'rgba(72,82, 175, 0.2)',
          },
          isLoading: acceptPendingshiftIsFetching,
          onClick: acceptPendingShift,
        }}
      />

      {/* <FilterGroup handleSortChange={handleSortChange} handleSearchInputChange={handleSearchInputChange} entity="Job" /> */}

      <Table
        columns={columns}
        data={data?.payload || []}
        paginationData={data?.meta_data?.pagination}
        isLoading={status === 'loading'}
        enableRowSelect
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default PendingShift;
